'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useDispatch } from 'react-redux';
import { ERROR_TYPES, parseApiError } from '@/constants/errorTypes';
import { logout } from '@/app/redux/slices/authSlice';
import { OfflineBanner } from './ErrorDisplay';

/**
 * Hook for handling errors with automatic retry and navigation
 * @param {Object} options - Configuration options
 * @returns {Object} Error handling utilities
 */
export function useErrorHandler(options = {}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { onLogout, maxRetries = 3 } = options;

  const [retryCount, setRetryCount] = useState(0);

  const handleError = useCallback(
    (error, context = {}) => {
      const parsedError = parseApiError(error);
      console.error(`[ErrorHandler] ${context.operation || 'Operation'} failed:`, parsedError);

      // Handle session expiry / unauthorized
      if (
        parsedError.type === ERROR_TYPES.SESSION_EXPIRED ||
        parsedError.type === ERROR_TYPES.UNAUTHORIZED ||
        parsedError.type === ERROR_TYPES.INVALID_TOKEN
      ) {
        if (onLogout) {
          onLogout();
        } else {
          dispatch(logout());
        }
        router.push('/login');
        return parsedError;
      }

      // Handle account issues that require redirect
      if (
        parsedError.type === ERROR_TYPES.ACCOUNT_SUSPENDED ||
        parsedError.type === ERROR_TYPES.ACCESS_REVOKED
      ) {
        if (onLogout) {
          onLogout();
        }
        router.push('/support');
        return parsedError;
      }

      return parsedError;
    },
    [dispatch, router, onLogout]
  );

  const createRetryHandler = useCallback(
    (operation, onError) => {
      return async (...args) => {
        try {
          const result = await operation(...args);
          setRetryCount(0);
          return result;
        } catch (error) {
          const parsedError = handleError(error, { operation: operation.name });

          if (parsedError.recoverable && retryCount < maxRetries) {
            setRetryCount((prev) => prev + 1);
          }

          if (onError) {
            onError(parsedError);
          }

          throw parsedError;
        }
      };
    },
    [handleError, retryCount, maxRetries]
  );

  return {
    handleError,
    createRetryHandler,
    retryCount,
    canRetry: retryCount < maxRetries,
    resetRetryCount: () => setRetryCount(0),
  };
}

/**
 * Hook for detecting online/offline status
 * @returns {Object} Network status
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        // Could trigger a reconnection callback here
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  return { isOnline, wasOffline };
}

/**
 * Component that provides network status awareness
 */
export function NetworkStatusProvider({ children }) {
  const { isOnline } = useNetworkStatus();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShowBanner(true);
    } else {
      // Delay hiding to show reconnection
      const timer = setTimeout(() => setShowBanner(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <>
      {showBanner && !isOnline && <OfflineBanner onRetry={handleRetry} />}
      {children}
    </>
  );
}

export default useErrorHandler;
